import React from 'react';
import classnames from 'classnames';
import { CheckmarkIcon } from '../icons/icons';
import { Icon } from '../icon';
import Colors from '../colors';
import styles from './checkbox.css';

interface CheckboxProps {
  /** Checkbox is selected */
  checked?: boolean;
  /** Override checkbox background color (default to unbounceBlue) */
  color?: Colors;
  /** Label for the checkbox */
  label?: string;
  /** Adds required attribute */
  required?: boolean;
  /** Disables checkbox disallowing interaction */
  disabled?: boolean;
  /** Adds additional classes to the component. */
  className?: string;
  /** Callback when checkbox is clicked */
  onClick?: () => void;
  /** Name for input field */
  name?: string;
  /** Highlight the label of the selected element */
  highlight?: boolean;
  /** Provides a data-testid */
  'data-testid'?: string;
  /** Unique identifier */
  key?: string;
}

const CHECKMARK_SIZE = '8px';

/**
 * Checkbox Component
 *
 * @param {CheckboxProps} props
 *
 * @returns JSX.ELEMENT
 */
export const Checkbox = ({
  disabled,
  className,
  onClick,
  label,
  highlight,
  key,
  name = '',
  required,
  checked = false,
  color = Colors.UnbounceBlue,
  'data-testid': dataTestId = 'checkbox'
}: CheckboxProps) => {
  const radioClasses = classnames(
    styles.container,
    {
      [styles.disabled]: disabled,
      [styles.checked]: checked
    },
    className
  );

  const labelClasses = classnames(styles.label, {
    [styles.highlight]: highlight && checked
  });

  const checkboxClasses = classnames(styles.checkbox, styles[color]);

  return (
    <div
      className={radioClasses}
      data-testid={dataTestId}
      key={key}
      {...(!disabled && onClick ? { onClick } : {})}
    >
      <div className={checkboxClasses} data-testid="checkbox-icon">
        <Icon color={disabled ? Colors.Gallery : Colors.White} size={CHECKMARK_SIZE}>
          <CheckmarkIcon data-testid="checkmark" />
        </Icon>
      </div>
      <input
        type={'checkbox'}
        className={styles.input}
        checked={!!checked}
        required={required}
        disabled={disabled}
        name={name}
        readOnly
      />
      {label && <label className={labelClasses}>{label}</label>}
    </div>
  );
};

export default Checkbox;
